import dayjs from 'dayjs';
import Metrics from './components/Metrics';
import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import SyncLogs from './components/SyncLogs';
import Select from '@/components/Common/Select';
import ToolErrors from './components/ToolErrors';
import ResponseTime from './components/ResponseTime';
import CallNeedingAudit from './components/CallNeedingAudit';
import { BusinessProfileResponse } from '@/apis/business/types';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import DateRangePicker from '@/components/Common/DateRangePicker';
import { useGetAgencyAgents, useGetBusinesses } from '@/apis/agency/hook';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';

export default function SystemSentiment() {
  const theme = useTheme();

  const { control, watch, setValue } = useForm({
    defaultValues: {
      business_id: ' ',
      agent_id: ' ',
      date: `${dayjs().subtract(1, 'day').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}`
    }
  });

  const values = watch();
  const { date, ...rest } = values;

  const [start_date, end_date] = date?.split(',') || [];

  const filters = {
    ...rest,
    start_date,
    end_date
  };
  const mainFiltersKey = `${filters.business_id}|${filters.agent_id}|${filters.start_date}|${filters.end_date}`;

  const { data: businesses, isLoading } = useGetBusinesses({ per_page: 9999 });
  const { data: agencyAgents, isLoading: loadingAgents } = useGetAgencyAgents({
    business_id: values?.business_id,
    per_page: 9999
  });
  const { data: agencyOrganization } = useGetAgencyOrganizations();

  const tillDateCreatedAt = useMemo(() => {
    if (values.agent_id && values.agent_id !== ' ' && agencyAgents?.data?.length) {
      const agent = agencyAgents.data.find(
        (a: { id: number; created_at?: string }) => String(a.id) === String(values.agent_id)
      );
      if (agent?.created_at) return agent.created_at;
    }
    if (values.business_id && values.business_id !== ' ' && businesses?.data?.length) {
      const business = businesses.data.find(
        (b: BusinessProfileResponse) => String(b.id) === String(values.business_id)
      );
      if (business?.created_at) return business.created_at;
    }
    return agencyOrganization?.created_at;
  }, [values.agent_id, values.business_id, agencyAgents?.data, businesses?.data, agencyOrganization?.created_at]);

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' mb={3}>
        <Box>
          <Typography fontSize={20} fontWeight={500}>
            System Sentiment
          </Typography>
          <Typography fontSize={14} color='text.secondary'>
            AI agent technical performance & quality monitoring
          </Typography>
        </Box>
        <Stack direction='row' gap={2}>
          <Box width={260}>
            <DateRangePicker name='date' control={control} createdAt={tillDateCreatedAt} />
          </Box>
          <Box width={180}>
            <Select
              name='business_id'
              label='Business'
              disabled={isLoading}
              control={control}
              searchable
              sortOrder='none'
              onChange={({ target }: any) => {
                setValue('business_id', target.value);
                setValue('agent_id', ' ');
              }}
              options={[
                { value: ' ', label: 'All Business' },
                ...(businesses?.data.map((business: BusinessProfileResponse) => ({
                  value: business.id,
                  label: business.name
                })) ?? [])
              ]}
              sx={{ height: '39px', background: theme.palette.background.paper }}
            />
          </Box>
          <Box width={180}>
            <Select
              name='agent_id'
              label='Agents'
              sortOrder='none'
              disabled={loadingAgents}
              control={control}
              options={[
                { value: ' ', label: 'All Agents' },
                ...(agencyAgents?.data.map((agent: any) => ({
                  value: agent.id,
                  label: agent.name
                })) ?? [])
              ]}
              sx={{ height: '39px', background: theme.palette.background.paper }}
            />
          </Box>
        </Stack>
      </Box>
      <Metrics filters={filters} />
      <CallNeedingAudit key={`audit-${mainFiltersKey}`} filters={filters} />
      <Box display='flex' gap={2} mb={3}>
        <ToolErrors key={`tool-errors-${mainFiltersKey}`} filters={filters} />
        <ResponseTime key={`response-time-${mainFiltersKey}`} filters={filters} />
      </Box>
      <SyncLogs key={`sync-logs-${mainFiltersKey}`} filters={filters} />
    </Box>
  );
}
